import { Camera, Scene } from 'babylonjs';
import type { PlaneBasis, RegionBounds2D, Vec2, Vec3 } from '../../solver/types';
import type { RectState } from '../../core/types';
import { cssToRay } from '../projection';
import { dragCorner, dragRotate, dragSide, planePointFromRay, worldToPlane, type RectCorner, type RectSide } from './rect.math';

export type RectHandle = RectSide | RectCorner | 'rotate' | 'move';

const CORNERS: readonly RectCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const isCorner = (h: RectHandle): h is RectCorner => (CORNERS as readonly string[]).includes(h);

/**
 * Owns the capture RectState (plane-space) and applies pointer-driven handle
 * drags. The React overlay converts a pointer position to CSS coords and calls
 * `drag(handle, x, y)`; this resolves the pointer ray against the plane and runs
 * the pure rect math.
 */
export class RectController {
  private rectState: RectState = { center: [0, 0], halfW: 1, halfH: 1, angle: 0 };
  private basis: PlaneBasis | null = null;
  private minHalf = 0.01;
  private grabOffset = 0;
  private moveGrab: Vec2 = [0, 0];

  constructor(
    private scene: Scene,
    private getCamera: () => Camera,
    private onChange: (rect: RectState) => void,
  ) {}

  init(basis: PlaneBasis, bounds: RegionBounds2D, modelDiag: number): void {
    this.basis = basis;
    // Effectively no lower bound on rect size (tiny models need tiny rects) — just
    // a hair above zero to avoid a degenerate, uncapturable 0-size rect.
    this.minHalf = Math.max(1e-4 * modelDiag, 1e-6);
    const cx = (bounds.min[0] + bounds.max[0]) / 2;
    const cy = (bounds.min[1] + bounds.max[1]) / 2;
    this.rectState = {
      center: [cx, cy],
      halfW: Math.max((bounds.max[0] - bounds.min[0]) / 2, this.minHalf),
      halfH: Math.max((bounds.max[1] - bounds.min[1]) / 2, this.minHalf),
      angle: 0,
    };
    this.onChange(this.rectState);
  }

  get rect(): RectState {
    return this.rectState;
  }

  get planeBasis(): PlaneBasis | null {
    return this.basis;
  }

  private planePoint(cssX: number, cssY: number): Vec2 | null {
    if (!this.basis) return null;
    const ray = cssToRay(this.scene, this.getCamera(), cssX, cssY);
    const origin: Vec3 = [ray.origin.x, ray.origin.y, ray.origin.z];
    const dir: Vec3 = [ray.direction.x, ray.direction.y, ray.direction.z];
    const hit = planePointFromRay(origin, dir, this.basis.origin, this.basis.normal);
    return hit ? worldToPlane(this.basis, hit) : null;
  }

  beginDrag(handle: RectHandle, cssX: number, cssY: number): void {
    const p = this.planePoint(cssX, cssY);
    if (!p) return;
    if (handle === 'rotate') {
      this.grabOffset = Math.atan2(p[1] - this.rectState.center[1], p[0] - this.rectState.center[0]) - this.rectState.angle;
    } else if (handle === 'move') {
      this.moveGrab = [p[0] - this.rectState.center[0], p[1] - this.rectState.center[1]];
    }
  }

  drag(handle: RectHandle, cssX: number, cssY: number): void {
    const p = this.planePoint(cssX, cssY);
    if (!p) return;
    if (handle === 'rotate') {
      this.rectState = dragRotate(this.rectState, p, this.grabOffset);
    } else if (handle === 'move') {
      this.rectState = { ...this.rectState, center: [p[0] - this.moveGrab[0], p[1] - this.moveGrab[1]] };
    } else if (isCorner(handle)) {
      this.rectState = dragCorner(this.rectState, handle, p, this.minHalf);
    } else {
      this.rectState = dragSide(this.rectState, handle, p, this.minHalf);
    }
    this.onChange(this.rectState);
  }
}
